var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var PeriodAddConfig_1;
/* eslint-disable import/no-duplicates */
import '@symon-ai/wisepipe-base-pipe'; // force import for JsonSchemaV2
import { PeriodAddType as PeriodAddTypeOrig, ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
import { validateSourceWithDetail } from './validation/validateHelper';
import { periodAddConfigSchemaValidation } from '../validations.generated';
export var PeriodAddType = PeriodAddTypeOrig;
export const periodAddConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:PeriodAddConfig',
    type: 'object',
    properties: {
        periodColumn: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:ColumnToolVariable'
                },
                { type: 'string', minLength: 1, maxLength: 150 }
            ]
        },
        alias: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:StringToolVariable'
                },
                { type: 'string', minLength: 1, maxLength: 150 }
            ]
        },
        addType: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:EnumToolVariable'
                },
                { type: 'string', enum: Object.values(PeriodAddType) }
            ]
        },
        intervalsColumn: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:ColumnToolVariable'
                },
                { type: 'string', minLength: 1, maxLength: 150 }
            ]
        },
        intervalsNumber: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:NumberToolVariable'
                },
                { type: 'number' }
            ]
        }
    },
    required: ['periodColumn', 'alias', 'addType'],
    allOf: [
        {
            if: { properties: { addType: { const: PeriodAddType.Column } } },
            then: { required: ['intervalsColumn'] }
        },
        {
            if: { properties: { addType: { const: PeriodAddType.Number } } },
            then: { required: ['intervalsNumber'] }
        }
    ]
};
let PeriodAddConfig = PeriodAddConfig_1 = class PeriodAddConfig extends ToolConfig {
    constructor(periodColumn, alias, addType, intervalsColumn, intervalsNumber, customFormats) {
        super(ToolType.PERIOD_ADD, customFormats);
        this.periodColumn = periodColumn;
        this.alias = alias;
        this.addType = addType;
        this.intervalsColumn = intervalsColumn;
        this.intervalsNumber = intervalsNumber;
        JSON.validate(this, PeriodAddConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return PeriodAddConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return PeriodAddConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return PeriodAddConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return PeriodAddConfig_1.outs;
    }
    static get in() {
        return PeriodAddConfig_1.ins[0];
    }
    static get out() {
        return PeriodAddConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.PERIOD_ADD.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE], this.ins, this.outs, ['date', 'incentives', 'period', 'add', 'months', 'quarters', 'years', 'timeshift'], true);
    }
    applyParameters(parameters) {
        const baseVals = this.getBaseValues(parameters);
        return new PeriodAddConfig_1(baseVals.periodColumn, baseVals.alias, baseVals.addType, baseVals.intervalsColumn, baseVals.intervalsNumber, this.customFormats);
    }
    getBaseValues(parameters) {
        return {
            periodColumn: parameters.getStrVal(this.periodColumn),
            alias: parameters.getStrVal(this.alias),
            addType: parameters.getEnumVal(this.addType),
            intervalsColumn: parameters.getStrOrUndefVal(this.intervalsColumn),
            intervalsNumber: parameters.getNumOrUndefVal(this.intervalsNumber)
        };
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        const exp = tsb.export(this.out);
        const newColumnFormats = new Map();
        if (source) {
            const parameters = iConfigHelper.getParameters();
            const baseVals = this.getBaseValues(parameters);
            const dependents = this.getDependents(exp, this.in, Array.from(baseVals.periodColumn)) ?? [];
            if (this.addType === PeriodAddType.Column) {
                const column = exp.findSourceColumn(this.in, baseVals.intervalsColumn);
                dependents.push(column);
            }
            const sourcePeriodColumn = exp.findSourceColumn(this.in, baseVals.periodColumn);
            const sourcePeriodColumnMetadata = sourcePeriodColumn?.column.metadata;
            if (!sourcePeriodColumnMetadata) {
                throw new Error('Period column metadata is missing');
            }
            exp.copyAll(this.in).makeNew(new Column(baseVals.alias, 'period', sourcePeriodColumnMetadata), dependents, true);
            const srcColumn = exp.findSourceColumn(this.in, baseVals.periodColumn);
            if (srcColumn?.customFormat) {
                newColumnFormats.set(baseVals.alias, srcColumn.customFormat.copyWithAutoGeneratedTrue());
            }
        }
        exp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, ins) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, ins);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        const parameters = iConfigHelper.getParameters();
        const baseVals = this.getBaseValues(parameters);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        const inputs = new Map();
        inputs.set(baseVals.periodColumn, ['period']);
        if (baseVals.intervalsColumn !== undefined) {
            inputs.set(baseVals.intervalsColumn, ['number', 'decimal']);
        }
        const sourceValidationResult = validateSourceWithDetail(source, inputs, [baseVals.alias]);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
PeriodAddConfig = PeriodAddConfig_1 = __decorate([
    JsonSchemaV2(periodAddConfigSchema, periodAddConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.PERIOD_ADD)
], PeriodAddConfig);
export { PeriodAddConfig };
//# sourceMappingURL=periodAddConfig.js.map